import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  TextField,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Autocomplete
} from '@mui/material';
import {
  History as ActivitiesIcon,
  Search as SearchIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const ActivitiesReport = () => {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [period, setPeriod] = useState('daily');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/reports/users');
      setUsers(response.data.data || []);
      // If only one user (self), select it automatically
      if (response.data.data?.length === 1) {
        setSelectedUser(response.data.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('period', period);
      if (selectedUser) params.append('user_id', selectedUser.id);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await api.get(`/reports/activities?${params.toString()}`);
      setReportData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch activities report:', error);
    } finally {
      setLoading(false);
    }
  }, [period, selectedUser, startDate, endDate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (selectedUser || !isAdmin) {
      fetchReport();
    }
  }, [fetchReport, selectedUser, isAdmin]);

  const getModuleColor = (module) => {
    const colors = {
      contacts: 'primary',
      accounts: 'secondary',
      jobs: 'success',
      documents: 'info',
      auth: 'default',
      recruiting: 'warning',
      activities: 'error'
    };
    return colors[module] || 'default';
  };

  const getActionIcon = (action) => {
    const icons = {
      create: '➕',
      update: '✏️',
      delete: '🗑️',
      view: '👁️',
      upload: '📤',
      download: '📥',
      login: '🔐',
      logout: '🚪',
      idle_time_reported: '⏸️',
      idle_time_resumed: '▶️'
    };
    return icons[action] || '📋';
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ActivitiesIcon sx={{ fontSize: 32 }} />
        Activities Report
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {users.length > 1 && (
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => `${option.name} (${option.email})`}
                  onChange={(event, newValue) => setSelectedUser(newValue)}
                  value={selectedUser}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Employee"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} md={users.length > 1 ? 2 : 3}>
              <TextField
                select
                fullWidth
                label="Period"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                size="small"
              >
                <MenuItem value="daily">Today</MenuItem>
                <MenuItem value="weekly">This Week</MenuItem>
                <MenuItem value="monthly">This Month</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={users.length > 1 ? 2 : 3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={users.length > 1 ? 2 : 3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={users.length > 1 ? 3 : 3}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={fetchReport}
                fullWidth
                sx={{ height: '40px' }}
              >
                Generate Report
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : reportData ? (
        <>
          {/* User Info */}
          {reportData.user && (
            <Card sx={{ mb: 3, bgcolor: 'primary.dark', color: 'white' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h6">
                  {reportData.user.name}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {reportData.user.email} • {reportData.dateRange.start} to {reportData.dateRange.end}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Summary */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Activities</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {reportData.summary.totalActivities}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>By Module</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {reportData.activitiesByModule.map((item) => (
                      <Chip
                        key={item.module}
                        label={`${item.module}: ${item.count}`}
                        color={getModuleColor(item.module)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>By Action</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {reportData.activitiesByAction.map((item) => (
                      <Chip
                        key={item.action}
                        label={`${getActionIcon(item.action)} ${item.action}: ${item.count}`}
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Activities Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Recent Activities
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      {users.length > 1 && <TableCell>User</TableCell>}
                      <TableCell>Module</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Entity</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.recentActivities.map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(activity.performed_at).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        {users.length > 1 && (
                          <TableCell>
                            {activity.user ? `${activity.user.first_name} ${activity.user.last_name}` : 'N/A'}
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            size="small"
                            label={activity.module}
                            color={getModuleColor(activity.module)}
                          />
                        </TableCell>
                        <TableCell>
                          {getActionIcon(activity.action)} {activity.action}
                        </TableCell>
                        <TableCell>{activity.entity_name || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" noWrap title={activity.description}>
                            {activity.description}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {reportData.recentActivities.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={users.length > 1 ? 6 : 5} align="center">
                          <Typography color="text.secondary" sx={{ py: 2 }}>
                            No activities found for this period.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      ) : null}
    </Box>
  );
};

export default ActivitiesReport;
